import PropTypes from 'prop-types';
import React from 'react';
import { Button } from 'react-bootstrap';
import { MapPin, Pencil } from 'lucide-react';

import Thumbnail from 'components/Thumbnails/Thumbnail';

import styles from './DashboardHero.module.scss';

/**
 * Hero section for the Client ATS Dashboard. Shows a welcome message,
 * user avatar, company and location on a blue gradient background.
 */
const DashboardHero = ({
  user,
  showNextStepsButton,
  profileCompletionPercentage,
  onNextStepsClick,
}) => {
  const displayName =
    user?.displayname &&
    user.displayname.trim() &&
    user.displayname.toLowerCase() !== 'client'
      ? user.displayname.trim()
      : '';
  const company = user?.organisation || false;
  const locationText =
    user?.location?.text &&
    user.location.text.trim() &&
    user.location.text.toLowerCase() !== 'private'
      ? user.location.text.trim()
      : '';

  const hasPersonalizedWelcome = !!displayName && !company;
  const welcomeText = hasPersonalizedWelcome
    ? 'Welcome back, '
    : 'Welcome back!';

  const clampedPercentage = Math.min(
    100,
    Math.max(0, profileCompletionPercentage || 0)
  );
  const circumference = 2 * Math.PI * 10;
  const progressOffset = circumference * (1 - clampedPercentage / 100);

  return (
    <section className={`${styles.hero}`} aria-label="Welcome">
      <div className={styles.content}>
        <div className={styles.leftBlock}>
          <div className={styles.avatarWrapper}>
            <Thumbnail
              thumbnails={user.avatars}
              className={styles.avatar}
              circle
            />
          </div>
          <div className={styles.textBlock}>
            <h2 className={styles.welcomeText}>
              {welcomeText}
              {hasPersonalizedWelcome && (
                <span className={styles.welcomeName}>
                  {displayName}!
                  {typeof onNextStepsClick === 'function' &&
                    !showNextStepsButton && (
                      <button
                        type="button"
                        className={styles.editProfileIconButton}
                        onClick={onNextStepsClick}
                        aria-label="Edit profile"
                      >
                        <Pencil width={20} height={20} aria-hidden="true" />
                      </button>
                    )}
                </span>
              )}
              {!hasPersonalizedWelcome &&
                typeof onNextStepsClick === 'function' &&
                !showNextStepsButton && (
                  <button
                    type="button"
                    className={styles.editProfileIconButton}
                    onClick={onNextStepsClick}
                    aria-label="Edit profile"
                  >
                    <Pencil width={20} height={20} aria-hidden="true" />
                  </button>
                )}
            </h2>
            <div className={styles.metaLine}>
              {company && displayName && (
                <span className={styles.company}>{displayName}</span>
              )}
              {company && displayName && locationText && (
                <span className={styles.separator}>•</span>
              )}
              {locationText && (
                <span className={styles.location}>
                  <MapPin
                    width={14}
                    height={14}
                    className={styles.locationIcon}
                    aria-hidden="true"
                  />
                  {locationText}
                </span>
              )}
            </div>
          </div>
        </div>
        {typeof onNextStepsClick === 'function' && showNextStepsButton && (
          <Button
            bsStyle="primary"
            className={styles.nextStepsButton}
            onClick={onNextStepsClick}
          >
            <div className={styles.circularProgress}>
              <svg
                className={styles.circularProgressSvg}
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="3"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#2eb85c"
                  strokeWidth="3"
                  strokeDasharray={`${circumference}`}
                  strokeDashoffset={`${progressOffset}`}
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className={styles.nextStepsText}>
              <div className={styles.nextStepsTitle}>Complete your profile</div>
              <div className={styles.nextStepsSubtitle}>
                {clampedPercentage}% complete
              </div>
            </div>
          </Button>
        )}
      </div>
    </section>
  );
};

DashboardHero.propTypes = {
  user: PropTypes.object,
  showNextStepsButton: PropTypes.bool,
  profileCompletionPercentage: PropTypes.number,
  onNextStepsClick: PropTypes.func,
};

export default DashboardHero;
